import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, Loader2, User as UserIcon, Phone, Mail, X } from "lucide-react";

const SERVICE_DOT = {
  terapia_fisica: "bg-green-500",
  rehabilitacion: "bg-green-500",
  quiropractica: "bg-emerald-500",
  terapias_alternativas: "bg-amber-500",
  estetica: "bg-rose-500",
  consulta_general: "bg-slate-500",
};

export const PatientSearch = ({ API, token, onSelect }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/admin/patient/search?q=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setResults(r.data?.items || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q, API, token]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full max-w-md" data-testid="patient-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Buscar paciente por nombre, teléfono o email…"
          className="w-full rounded-full border border-slate-200 bg-white pl-9 pr-9 py-2 text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100"
          data-testid="patient-search-input"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setResults([]); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="patient-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {loading && (
          <Loader2 className="absolute right-9 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500 animate-spin" />
        )}
      </div>

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-900/10 overflow-hidden" data-testid="patient-search-dropdown">
          {results.length === 0 && !loading && (
            <div className="px-4 py-6 text-center text-sm text-slate-500" data-testid="patient-search-empty">
              Sin resultados para “{q}”
            </div>
          )}
          {results.map((p, idx) => (
            <button
              key={`${p.phone}-${p.email}-${idx}`}
              type="button"
              onClick={() => {
                setOpen(false);
                onSelect({ name: p.name, phone: p.phone, email: p.email });
              }}
              className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
              data-testid={`patient-search-result-${idx}`}
            >
              <div className="h-9 w-9 rounded-full bg-green-100 text-green-700 grid place-items-center font-bold flex-shrink-0">
                {(p.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-semibold text-slate-900 truncate">{p.name || "(sin nombre)"}</p>
                  <p className="text-[10px] text-slate-400 flex-shrink-0">
                    {p.count} cita{p.count === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
                  {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>}
                  {p.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{p.email}</span>}
                </div>
                {p.services?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {p.services.slice(0, 4).map((s) => (
                      <span key={s} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700">
                        <span className={`h-1.5 w-1.5 rounded-full ${SERVICE_DOT[s] || "bg-slate-400"}`} />
                        {s.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
                {p.last_date && (
                  <p className="mt-1 text-[10px] text-slate-400">Última cita: {p.last_date}</p>
                )}
              </div>
              <UserIcon className="h-4 w-4 text-slate-300 flex-shrink-0 mt-1" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientSearch;
